import {
  Toaster as ChakraToaster,
  Box,
  Icon,
  Portal,
  Spinner,
  Toast,
  createToaster,
} from '@chakra-ui/react';
import React, { useCallback, useSyncExternalStore } from 'react';
import shadows from '@/theme/foundations/shadows';

/**
 * Compatibility shim for Chakra v2's useToast on top of v3's createToaster.
 * Call sites keep the v2 shape — `const toast = useToast(); toast({ title,
 * description, status, position, duration, isClosable })` — only the import
 * changes to `@/shared/hooks/useToast`.
 *
 * v3 fixes placement per toaster instance, so instances are created lazily
 * per position and <AppToasters /> (mounted once in src/index.tsx) re-renders
 * when a new placement first appears.
 */
export type ToastPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right';

export interface CompatToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  status?: 'success' | 'error' | 'warning' | 'info' | 'loading';
  position?: ToastPosition;
  duration?: number | null;
  isClosable?: boolean;
  id?: string;
}

const placementMap = {
  top: 'top',
  'top-left': 'top-start',
  'top-right': 'top-end',
  bottom: 'bottom',
  'bottom-left': 'bottom-start',
  'bottom-right': 'bottom-end',
} as const;

// v2's Alert icons (@chakra-ui/alert 2.0.11), filled. v3's Toast.Indicator
// draws outline glyphs and has no info icon (renders null).
const warningPath =
  'M11.983,0a12.206,12.206,0,0,0-8.51,3.653A11.8,11.8,0,0,0,0,12.207,11.779,11.779,0,0,0,11.8,24h.214A12.111,12.111,0,0,0,24,11.791h0A11.766,11.766,0,0,0,11.983,0ZM10.5,16.542a1.476,1.476,0,0,1,1.449-1.53h.027a1.527,1.527,0,0,1,1.523,1.47,1.475,1.475,0,0,1-1.449,1.53h-.027A1.529,1.529,0,0,1,10.5,16.542ZM11,12.5v-6a1,1,0,0,1,2,0v6a1,1,0,1,1-2,0Z';
const statusIconPaths: Record<string, string> = {
  success:
    'M12,0A12,12,0,1,0,24,12,12.014,12.014,0,0,0,12,0Zm6.927,8.2-6.845,9.289a1.011,1.011,0,0,1-1.43.188L5.764,13.769a1,1,0,1,1,1.25-1.562l4.076,3.261,6.227-8.451A1,1,0,1,1,18.927,8.2Z',
  info: 'M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm.25,5a1.5,1.5,0,1,1-1.5,1.5A1.5,1.5,0,0,1,12.25,5ZM14.5,18.5h-4a1,1,0,0,1,0-2h.75a.25.25,0,0,0,.25-.25v-4.5a.25.25,0,0,0-.25-.25H10.5a1,1,0,0,1,0-2h1a2,2,0,0,1,2,2v4.75a.25.25,0,0,0,.25.25h.75a1,1,0,1,1,0,2Z',
  warning: warningPath,
  error: warningPath,
};

// v2's icon box is 20×24 (w 5, h 6), so the 20px glyph centres on the 24px
// title line. v3 Icon is asChild by default, so the <svg> must be its child.
const StatusIcon = ({ type }: { type?: string }) => (
  <Icon w="5" h="6" flexShrink="0">
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d={statusIconPaths[type ?? 'info'] ?? statusIconPaths.info}
      />
    </svg>
  </Icon>
);

const toasters = new Map<string, ReturnType<typeof createToaster>>();
const listeners = new Set<() => void>();

const getToaster = (position: ToastPosition) => {
  const placement = placementMap[position];
  let toaster = toasters.get(placement);
  if (!toaster) {
    // v2 kept toasts 0.5rem from the viewport edge (toastSpacing margin);
    // v3's default offset is 1rem. The 16px gap between toasts matches.
    toaster = createToaster({
      placement,
      pauseOnPageIdle: true,
      offsets: '0.5rem',
    });
    toasters.set(placement, toaster);
    listeners.forEach((notify) => notify());
  }
  return toaster;
};

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};
const getSnapshot = () => toasters.size;

/** Toast outlets — v2's provider mounted these automatically, v3 does not. */
export const AppToasters: React.FC = () => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <Portal>
      {[...toasters.entries()].map(([placement, toaster]) => (
        <ChakraToaster key={placement} toaster={toaster}>
          {(toast) => (
            // v2 toast: Alert variant="solid" (@chakra-ui/toast 4.0.0). Box
            // as v2: py 12px, pe 32px, radius md, the portal's lg shadow
            // (the `lg` token now resolves to v3's semantic shadow). Width
            // follows the content from 300px up: staging's
            // contacts/list/overrides.scss lifted v2's 560px cap to
            // fit-content (it targets a v2 class hash, dead under v3).
            <Toast.Root
              width="max-content"
              minWidth="300px"
              maxWidth="calc(100vw - 1rem)"
              py="3"
              pe="8"
              borderRadius="md"
              boxShadow={shadows.lg}
              // v2 used {status colour}.500 with white text; v3's recipe uses
              // .solid (= .600, darker in the portal palette) and has no
              // info/loading rule (falls back to bg.panel/white).
              css={{
                '&[data-type=success]': { bg: 'green.500' },
                '&[data-type=error]': { bg: 'red.500' },
                '&[data-type=warning]': { bg: 'orange.500' },
                '&[data-type=info], &[data-type=loading]': {
                  bg: 'blue.500',
                  color: 'white',
                  '--toast-trigger-bg': '{white/10}',
                  '--toast-border-color': '{white/40}',
                },
              }}
            >
              {toast.type === 'loading' ? (
                <Spinner size="md" />
              ) : (
                <StatusIcon type={toast.type} />
              )}
              {/* v2: plain block wrapper, no gap; title bold, both 16px/24px */}
              <Box flex="1" maxWidth="100%">
                {toast.title && (
                  <Toast.Title textStyle="md" fontWeight="bold">
                    {toast.title}
                  </Toast.Title>
                )}
                {toast.description && (
                  <Toast.Description display="block" textStyle="md" opacity="1">
                    {toast.description}
                  </Toast.Description>
                )}
              </Box>
              {/* v2 CloseButton size="sm": 24px box, 10px icon, full colour */}
              {toast.closable && (
                <Toast.CloseTrigger
                  boxSize="6"
                  p="0"
                  color="inherit"
                  fontSize="2xs"
                  borderRadius="md"
                  _hover={{ bg: 'blackAlpha.100' }}
                  _active={{ bg: 'blackAlpha.200' }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M.439,21.44a1.5,1.5,0,0,0,2.122,2.121L11.823,14.3a.25.25,0,0,1,.354,0l9.262,9.263a1.5,1.5,0,1,0,2.122-2.121L14.3,12.177a.25.25,0,0,1,0-.354l9.263-9.262A1.5,1.5,0,0,0,21.439.44L12.177,9.7a.25.25,0,0,1-.354,0L2.561.44A1.5,1.5,0,0,0,.439,2.561L9.7,11.823a.25.25,0,0,1,0,.354Z"
                    />
                  </svg>
                </Toast.CloseTrigger>
              )}
            </Toast.Root>
          )}
        </ChakraToaster>
      ))}
    </Portal>
  );
};

export const useToast = () => {
  return useCallback((options: CompatToastOptions) => {
    const {
      position = 'bottom',
      status = 'info',
      isClosable,
      duration,
      ...rest
    } = options;
    getToaster(position).create({
      ...rest,
      type: status,
      closable: isClosable,
      // v2 default duration was 5000ms; null meant "stay until dismissed"
      duration: duration === null ? Infinity : (duration ?? 5000),
    });
  }, []);
};
